import { Data, Effect } from 'effect';

export type StudioOperation =
  | 'api'
  | 'download'
  | 'pdf'
  | 'pdfjs'
  | 'range'
  | 'registry'
  | 'selection'
  | 'zip';

/**
 * Redacted browser-runtime failure. File bytes, names, API bodies, URLs, PDF
 * objects, and external causes stay outside the serializable error value.
 */
export class StudioRuntimeError extends Data.TaggedError('StudioRuntimeError')<{
  readonly operation: StudioOperation;
  readonly subject: string;
}> {}

const studioRuntimeCauses = new WeakMap<StudioRuntimeError, unknown>();

function studioRuntimeError(
  operation: StudioOperation,
  subject: string,
  cause: unknown,
): StudioRuntimeError {
  const error = new StudioRuntimeError({ operation, subject });
  studioRuntimeCauses.set(error, cause);
  return error;
}

export function attemptStudio<A>(
  operation: StudioOperation,
  subject: string,
  evaluate: () => A,
): Effect.Effect<A, StudioRuntimeError> {
  return Effect.try({
    try: evaluate,
    catch: (cause) => studioRuntimeError(operation, subject, cause),
  });
}

export function attemptStudioPromise<A>(
  operation: StudioOperation,
  subject: string,
  evaluate: () => PromiseLike<A>,
): Effect.Effect<A, StudioRuntimeError> {
  return Effect.tryPromise({
    try: evaluate,
    catch: (cause) => studioRuntimeError(operation, subject, cause),
  });
}

export function restoreStudioRuntimeError<A>(
  program: Effect.Effect<A, StudioRuntimeError>,
): Effect.Effect<A, unknown> {
  return program.pipe(
    Effect.catch((error) => Effect.fail(studioRuntimeCauses.get(error) ?? error)),
  );
}

export function runStudioSync<A>(program: Effect.Effect<A, StudioRuntimeError>): A {
  return Effect.runSync(restoreStudioRuntimeError(program));
}

export function runStudioPromise<A>(
  program: Effect.Effect<A, StudioRuntimeError>,
  options?: { signal?: AbortSignal },
): Promise<A> {
  return Effect.runPromise(restoreStudioRuntimeError(program), options);
}
